// src/pages/Admin/AdminProductsPage.jsx
import React, { useEffect, useState, useMemo, useRef } from "react";
import { adminService } from "../../services/admin.service";
import "./AdminProducts.css";

const HSN_GST_MAP = {
  85076000: 18,
  85044010: 18,
  85071000: 28,
  85079090: 18,
  85044030: 5, // ✅ ADDED: Battery Chargers
};

const defaultForm = {
  product_id: "",
  sku: "",
  stock_level: "",
  base_price: "",
  hsn_code: "85076000",
  country_of_origin: "India",
  product_dimension: "",
  product_weight: "",
  category_id: "",
  name: "",
  description: "",
  imagesText: "",
  technical_specs_text: "",
  isFeatured: false,
  showOnHome: false,
  showOnPopular: false,
  showOnRecentlyLaunched: false,
  is_visible: false,
};

const MAX_IMAGES = 5;

const AdminProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [form, setForm] = useState(defaultForm);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingProductId, setEditingProductId] = useState(null);

  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [categoriesError, setCategoriesError] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");

  const [imageFiles, setImageFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);

  const imageFilesRef = useRef(imageFiles);

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState("");
  const [lightboxAlt, setLightboxAlt] = useState("");

  const openLightbox = (src, alt = "") => {
    setLightboxSrc(src);
    setLightboxAlt(alt);
    setLightboxOpen(true);
    document.body.style.overflow = "hidden"; // Prevent background scroll behind lightbox
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setLightboxSrc("");
    setLightboxAlt("");
    document.body.style.overflow = ""; // Reset overflow to its stylesheet default
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
    };
    if (lightboxOpen) document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [lightboxOpen]);

  const getCategoryName = (categoryId) => {
    if (!categoryId) return "-";
    const cat = categories.find((c) => c.category_id === categoryId);
    return cat?.name || categoryId;
  };

  const extractStock = (p) => {
    const candidates = [
      p.stock_level,
      p.stockLevel,
      p.stock,
      p.quantity,
      p.available_stock,
    ];
    for (const val of candidates) {
      if (typeof val === "number") return val;
      if (typeof val === "string" && val.trim() !== "") {
        const n = Number(val);
        if (!Number.isNaN(n)) return n;
      }
    }
    return null;
  };

  const getManualUrlsFromForm = () =>
    (form.imagesText || "")
      .split(/[\n,]/)
      .map((s) => s.trim())
      .filter(Boolean);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setCategoriesLoading(true);
        setError("");
        setCategoriesError("");

        const [productsRes, categoriesRes] = await Promise.all([
          adminService.getProducts(),
          adminService.getCategories(),
        ]);

        const list = Array.isArray(productsRes)
          ? productsRes
          : Array.isArray(productsRes?.products)
            ? productsRes.products
            : [];
        setProducts(list);

        const cats = Array.isArray(categoriesRes)
          ? categoriesRes
          : Array.isArray(categoriesRes?.categories)
            ? categoriesRes.categories
            : [];
        setCategories(cats);
      } catch (err) {
        console.error("Failed to load admin products/categories:", err);
        setError("Failed to load products");
        setCategoriesError("Failed to load categories");
      } finally {
        setLoading(false);
        setCategoriesLoading(false);
      }
    };

    loadData();
  }, []);

  const reloadProducts = async () => {
    try {
      const data = await adminService.getProducts();
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.products)
          ? data.products
          : [];
      setProducts(list);
    } catch (err) {
      console.error("Failed to reload products:", err);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const buildPayloadFromForm = (finalImages) => {
    const productId =
      (form.product_id && String(form.product_id).trim()) ||
      `prod_${Date.now()}`;
    const name = form.name ? form.name.trim() : "";
    const categoryId = form.category_id ? form.category_id.trim() : "";
    const description = form.description ? form.description.trim() : "";

    const selectedGst = HSN_GST_MAP[form.hsn_code] || 18;
    const finalCalculatedPrice = form.base_price
      ? Number(form.base_price) * (1 + selectedGst / 100)
      : 0;

    const images =
      Array.isArray(finalImages) && finalImages.length > 0
        ? finalImages
        : getManualUrlsFromForm();
    const technical_specs = form.technical_specs_text
      ? { description: form.technical_specs_text }
      : undefined;

    const display_flags = [
      ...(form.isFeatured ? ["featured"] : []),
      ...(form.showOnHome ? ["home"] : []),
      ...(form.showOnPopular ? ["popular"] : []),
      ...(form.showOnRecentlyLaunched ? ["recently_launched"] : []),
    ];

    return {
      product_id: productId,
      sku: form.sku || null,
      stock_level: Number(form.stock_level || 0),
      base_price: Number(form.base_price || 0),
      current_price: finalCalculatedPrice,
      hsn_code: form.hsn_code,
      gst_rate: selectedGst,
      country_of_origin: form.country_of_origin,
      product_dimension: form.product_dimension,
      product_weight: Number(form.product_weight || 0),
      category_id: categoryId,
      name,
      description,
      images,
      technical_specs,
      display_flags,
      is_visible: form.is_visible,
    };
  };

  const resetForm = () => {
    imageFiles.forEach((f) => {
      if (f.previewUrl) URL.revokeObjectURL(f.previewUrl);
    });
    setForm(defaultForm);
    setEditingProductId(null);
    setImageFiles([]);
    setIsDragging(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");

    try {
      const manualUrls = getManualUrlsFromForm();
      const totalImagesCount = manualUrls.length + imageFiles.length;

      if (totalImagesCount === 0)
        throw new Error("Please add at least one product image.");
      if (totalImagesCount > MAX_IMAGES)
        throw new Error(
          `You can upload a maximum of ${MAX_IMAGES} images. Currently you have ${totalImagesCount}.`,
        );

      let uploadedImageUrls = [];
      if (imageFiles.length > 0) {
        for (const fileObj of imageFiles) {
          try {
            const res = await adminService.uploadProductImage(fileObj.file);
            const url = res?.url || res?.secure_url || res;
            if (url) uploadedImageUrls.push(url);
          } catch (uploadErr) {
            console.error("Failed to upload image:", uploadErr);
            throw uploadErr;
          }
        }
      }

      const allImages = [...manualUrls, ...uploadedImageUrls];
      const payload = buildPayloadFromForm(allImages);

      if (editingProductId) {
        await adminService.updateProduct(editingProductId, payload);
        await adminService.updateInventory(editingProductId, {
          stock_level: payload.stock_level,
          current_price: payload.current_price,
          base_price: payload.base_price,
          product_weight: payload.product_weight,
          product_dimension: payload.product_dimension,
        });
      } else {
        await adminService.createProduct(payload);
      }

      resetForm();
      setShowForm(false);
      await reloadProducts();
    } catch (err) {
      console.error("Failed to save product:", err);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to save product",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleVisibility = async (product) => {
    const id = product.product_id || product.id || product._id;
    if (!id) return;

    const newVisibility = !product.is_visible;

    setProducts((prev) =>
      prev.map((p) =>
        p.product_id === id || p._id === id
          ? { ...p, is_visible: newVisibility }
          : p,
      ),
    );

    try {
      await adminService.updateProduct(id, { is_visible: newVisibility });
    } catch (err) {
      console.error("Failed to update visibility:", err);
      setProducts((prev) =>
        prev.map((p) =>
          p.product_id === id || p._id === id
            ? { ...p, is_visible: !newVisibility }
            : p,
        ),
      );
      alert("Failed to update visibility on the server.");
    }
  };

  const handleDelete = async (product) => {
    const id = product.product_id || product.id || product._id;
    if (!id) return;
    if (!window.confirm("Delete this product?")) return;

    try {
      await adminService.deleteProduct(id);
      setProducts((prev) =>
        prev.filter((p) => p.product_id !== id && p.id !== id && p._id !== id),
      );
    } catch (err) {
      console.error("Failed to delete product:", err);
      alert(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to delete product",
      );
    }
  };

  const handleEdit = (p) => {
    resetForm();
    setEditingProductId(p.product_id || p._id || p.id || null);

    const imagesText = Array.isArray(p.images) ? p.images.join("\n") : "";
    const technical_specs_text =
      p.technical_specs?.description ||
      (p.technical_specs ? JSON.stringify(p.technical_specs, null, 2) : "");
    const display_flags = Array.isArray(p.display_flags) ? p.display_flags : [];
    const stockVal = extractStock(p);

    const gst = p.gst_rate || HSN_GST_MAP[p.hsn_code] || 18;
    const derivedBasePrice =
      p.base_price !== undefined && p.base_price !== 0
        ? p.base_price
        : p.price_display
          ? (p.price_display / (1 + gst / 100)).toFixed(2)
          : "";

    setForm({
      product_id: p.product_id || "",
      sku: p.sku || "",
      stock_level: stockVal === null ? "" : String(stockVal),
      base_price: derivedBasePrice,
      hsn_code: p.hsn_code || "85076000",
      country_of_origin: p.country_of_origin || "India",
      product_dimension: p.product_dimension || "",
      product_weight:
        p.product_weight !== undefined ? String(p.product_weight) : "",
      category_id: p.category_id || "",
      name: p.name || "",
      description: p.description || "",
      imagesText,
      technical_specs_text,
      isFeatured: display_flags.includes("featured"),
      showOnHome: display_flags.includes("home"),
      showOnPopular: display_flags.includes("popular"),
      is_visible: p.is_visible !== undefined ? p.is_visible : false,
      showOnRecentlyLaunched: display_flags.includes("recently_launched"),
    });

    setShowForm(true);
  };

  const appendFiles = (files) => {
    if (!files || files.length === 0) return;

    const currentManualCount = getManualUrlsFromForm().length;
    const currentFileCount = imageFiles.length;
    const remainingSlots = MAX_IMAGES - (currentManualCount + currentFileCount);

    if (remainingSlots <= 0) {
      setError(
        `You already have ${currentManualCount + currentFileCount} images. Max allowed is ${MAX_IMAGES}.`,
      );
      return;
    }

    const incoming = Array.from(files).slice(0, remainingSlots);

    const fileObjects = incoming.map((file, index) => ({
      id: `${file.name}-${Date.now()}-${index}`,
      file,
      name: file.name,
      size: file.size,
      previewUrl: URL.createObjectURL(file),
    }));

    setImageFiles((prev) => [...prev, ...fileObjects]);
  };

  const handleFileInputChange = (e) => appendFiles(e.target.files);

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      appendFiles(e.dataTransfer.files);
      e.dataTransfer.clearData();
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const removeImageFile = (fileId) => {
    setImageFiles((prev) => {
      const fileToRemove = prev.find((f) => f.id === fileId);
      if (fileToRemove?.previewUrl)
        URL.revokeObjectURL(fileToRemove.previewUrl);
      return prev.filter((f) => f.id !== fileId);
    });
  };

  const removeManualUrl = async (index) => {
    const urls = getManualUrlsFromForm();
    const urlToRemove = urls[index];
    const newUrls = urls.filter((_, i) => i !== index);
    setForm((prev) => ({ ...prev, imagesText: newUrls.join("\n") }));

    if (editingProductId && urlToRemove) {
      try {
        await adminService.deleteProductImage(editingProductId, urlToRemove);
        await reloadProducts();
      } catch (err) {
        console.error("Failed to delete image on server:", err);
      }
    }
  };

  useEffect(() => {
    imageFilesRef.current = imageFiles;
  }, [imageFiles]);

  useEffect(() => {
    return () => {
      imageFilesRef.current.forEach((f) => {
        if (f.previewUrl) URL.revokeObjectURL(f.previewUrl);
      });
    };
  }, []);

  const filteredProducts = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return products.filter((p) => {
      const matchesSearch =
        !term ||
        (p.name && p.name.toLowerCase().includes(term)) ||
        (p.sku && p.sku.toLowerCase().includes(term)) ||
        (p.product_id && String(p.product_id).toLowerCase().includes(term));
      const matchesCategory =
        !categoryFilter || p.category_id === categoryFilter;
      return matchesSearch && matchesCategory;
    });
  }, [products, searchTerm, categoryFilter]);

  const totalVisibleProducts = filteredProducts.length;
  const selectedCategory = categoryFilter
    ? categories.find((c) => c.category_id === categoryFilter)
    : null;

  const totalStockForSelectedCategory = useMemo(() => {
    if (!selectedCategory) return null;
    return filteredProducts.reduce((sum, p) => {
      const n = extractStock(p);
      return sum + (n === null ? 0 : n);
    }, 0);
  }, [filteredProducts, selectedCategory]);

  const toggleForm = () => {
    if (showForm) {
      resetForm();
      setShowForm(false);
    } else {
      resetForm();
      setShowForm(true);
    }
  };

  const previewManualUrls = getManualUrlsFromForm();
  const activeGstRate = HSN_GST_MAP[form.hsn_code] || 18;

  return (
    <div className="admin-products-page">
      <div className="admin-products-header">
        <div>
          <h1>Products</h1>
          <p className="admin-products-subtitle">
            Manage your product catalog, pricing and inventory.
          </p>
        </div>
        <button
          className="admin-btn primary admin-products-main-btn"
          onClick={toggleForm}
        >
          {showForm ? "Back to Product List" : "+ Add Product"}
        </button>
      </div>

      {error && <p className="admin-error-text">{error}</p>}

      {showForm && (
        <div className="admin-card add-product-card">
          <form className="add-product-form" onSubmit={handleSubmit}>
            <div className="add-product-left">
              <h2 className="section-title">Product Images</h2>
              <p className="helper-text">
                Drag &amp; drop files or browse. You can upload a total of{" "}
                <strong>{MAX_IMAGES}</strong> images.
              </p>

              <div
                className={"image-dropzone" + (isDragging ? " dragging" : "")}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
              >
                <div className="image-dropzone-icon">🖼️</div>
                <p className="image-dropzone-title">
                  Drag &amp; drop your images here
                </p>
                <p className="image-dropzone-subtitle">
                  or{" "}
                  <label className="image-browse-label">
                    Browse
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleFileInputChange}
                      style={{ display: "none" }}
                    />
                  </label>
                </p>
                <p className="image-dropzone-note">
                  Remaining slots:{" "}
                  {Math.max(
                    0,
                    MAX_IMAGES - (imageFiles.length + previewManualUrls.length),
                  )}
                </p>
              </div>

              {imageFiles.length > 0 && (
                <div className="image-preview-section">
                  <h3 className="section-subtitle">File Previews</h3>
                  <div className="image-preview-grid">
                    {imageFiles.map((file) => (
                      <div key={file.id} className="image-preview-item">
                        <button
                          type="button"
                          className="image-file-remove"
                          onClick={() => removeImageFile(file.id)}
                        >
                          ✕
                        </button>
                        <div className="image-preview-thumb-wrapper">
                          <img
                            src={file.previewUrl}
                            alt={file.name}
                            className="image-preview-thumb"
                            onClick={() =>
                              openLightbox(file.previewUrl, file.name)
                            }
                            style={{ cursor: "zoom-in" }}
                          />
                        </div>
                        <div className="image-preview-meta">
                          <div className="image-file-name">{file.name}</div>
                          <div className="image-file-size">
                            {(file.size / 1024).toFixed(0)} KB
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {previewManualUrls.length > 0 && (
                <div className="image-preview-section">
                  <h3 className="section-subtitle">Image Preview</h3>
                  <div className="image-preview-grid">
                    {previewManualUrls.map((url, idx) => (
                      <div key={url + idx} className="image-preview-item">
                        <button
                          type="button"
                          className="image-file-remove"
                          onClick={() => removeManualUrl(idx)}
                        >
                          ✕
                        </button>
                        <div className="image-preview-thumb-wrapper">
                          <img
                            src={url}
                            alt={`Img${idx + 1}`}
                            className="image-preview-thumb"
                            onClick={() => openLightbox(url, `Img${idx + 1}`)}
                            onError={(e) => {
                              e.target.style.display = "none";
                            }}
                            style={{ cursor: "zoom-in" }}
                          />
                        </div>
                        <div className="image-preview-meta">
                          <div className="image-file-name">{`Img${idx + 1}`}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <label className="admin-product-form-label">
                Image URLs (one per line or comma-separated)
                <textarea
                  className="admin-input textarea"
                  name="imagesText"
                  value={form.imagesText}
                  onChange={handleChange}
                  rows={4}
                  placeholder={"https://...\nhttps://..."}
                />
              </label>
            </div>

            <div className="add-product-right">
              <h2 className="section-title">
                {editingProductId ? "Edit Product" : "Product Details"}
              </h2>

              <div className="two-column-grid">
                <label className="admin-product-form-label">
                  Product Name *
                  <input
                    className="admin-input"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    required
                  />
                </label>

                <label className="admin-product-form-label">
                  Category *
                  <select
                    className="admin-input"
                    name="category_id"
                    value={form.category_id}
                    onChange={handleChange}
                    required
                  >
                    <option value="">-- Select Category --</option>
                    {categories.map((cat) => (
                      <option key={cat.category_id} value={cat.category_id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="two-column-grid">
                <label className="admin-product-form-label">
                  HSN Code *
                  <select
                    className="admin-input"
                    name="hsn_code"
                    value={form.hsn_code}
                    onChange={handleChange}
                    required
                  >
                    <option value="85076000">
                      85076000 (Lithium / LFP / EV - 18% GST)
                    </option>
                    <option value="85044010">
                      85044010 (Inverters - 18% GST)
                    </option>
                    <option value="85071000">
                      85071000 (Lead-Acid Starter - 28% GST)
                    </option>
                    <option value="85079090">
                      85079090 (BMS / Parts - 18% GST)
                    </option>
                    <option value="85044030">
                      85044030 (Battery Chargers - 5% GST)
                    </option>
                  </select>
                </label>

                <label className="admin-product-form-label">
                  Country of Origin *
                  <select
                    className="admin-input"
                    name="country_of_origin"
                    value={form.country_of_origin}
                    onChange={handleChange}
                    required
                  >
                    <option value="India">India</option>
                    <option value="China">China</option>
                    <option value="Taiwan">Taiwan</option>
                    <option value="South Korea">South Korea</option>
                  </select>
                </label>
              </div>

              <div className="two-column-grid">
                <label className="admin-product-form-label">
                  Base Price (Excl. GST) ₹ *
                  <input
                    className="admin-input"
                    type="number"
                    name="base_price"
                    value={form.base_price}
                    onChange={handleChange}
                    required
                    min="0"
                  />
                  {form.base_price && (
                    <div
                      style={{
                        marginTop: "8px",
                        fontSize: "12px",
                        color: "#16a34a",
                        fontWeight: "600",
                      }}
                    >
                      ↳ Final MRP (incl. {activeGstRate}% GST): ₹
                      {(
                        Number(form.base_price) *
                        (1 + activeGstRate / 100)
                      ).toFixed(2)}
                    </div>
                  )}
                </label>

                <label className="admin-product-form-label">
                  Stock Level *
                  <input
                    className="admin-input"
                    type="number"
                    name="stock_level"
                    value={form.stock_level}
                    onChange={handleChange}
                    required
                    min="0"
                  />
                </label>
              </div>

              <div className="two-column-grid">
                <label className="admin-product-form-label">
                  Weight (kg)
                  <input
                    className="admin-input"
                    type="number"
                    step="0.01"
                    name="product_weight"
                    value={form.product_weight}
                    onChange={handleChange}
                    placeholder="e.g. 15.5"
                  />
                </label>

                <label className="admin-product-form-label">
                  Dimensions (L x W x H)
                  <input
                    className="admin-input"
                    type="text"
                    name="product_dimension"
                    value={form.product_dimension}
                    onChange={handleChange}
                    placeholder="e.g. 30x20x15 cm"
                  />
                </label>
              </div>

              <div className="two-column-grid">
                <label className="admin-product-form-label">
                  Product ID
                  <input
                    className="admin-input"
                    name="product_id"
                    value={form.product_id}
                    onChange={handleChange}
                    placeholder="If empty, will be auto-generated"
                  />
                </label>

                <label className="admin-product-form-label">
                  SKU
                  <input
                    className="admin-input"
                    name="sku"
                    value={form.sku}
                    onChange={handleChange}
                    placeholder="e.g. ZYN-100AH"
                  />
                </label>
              </div>

              <label className="admin-product-form-label">
                Description
                <textarea
                  className="admin-input textarea"
                  name="description"
                  value={form.description}
                  onChange={handleChange}
                  rows={3}
                  required
                />
              </label>

              <label className="admin-product-form-label">
                Technical Specs (free text / JSON)
                <textarea
                  className="admin-input textarea"
                  name="technical_specs_text"
                  value={form.technical_specs_text}
                  onChange={handleChange}
                  rows={3}
                  placeholder='e.g. "12V 100Ah LiFePO4, 3000 cycles"'
                />
              </label>

              <div className="flags-row">
                <label
                  className="flag-checkbox"
                  style={{
                    borderRight: "2px solid #ddd",
                    paddingRight: "15px",
                    marginRight: "5px",
                  }}
                >
                  <input
                    type="checkbox"
                    name="is_visible"
                    checked={form.is_visible}
                    onChange={handleChange}
                  />
                  <span
                    style={{
                      fontWeight: "bold",
                      color: form.is_visible ? "#16a34a" : "#dc2626",
                    }}
                  >
                    {form.is_visible
                      ? "👁️ Visible on Store"
                      : "🚫 Hidden Draft"}
                  </span>
                </label>
                <label className="flag-checkbox">
                  <input
                    type="checkbox"
                    name="isFeatured"
                    checked={form.isFeatured}
                    onChange={handleChange}
                  />
                  <span>Featured</span>
                </label>
                <label className="flag-checkbox">
                  <input
                    type="checkbox"
                    name="showOnHome"
                    checked={form.showOnHome}
                    onChange={handleChange}
                  />
                  <span>Show on Home</span>
                </label>
                <label className="flag-checkbox">
                  <input
                    type="checkbox"
                    name="showOnPopular"
                    checked={form.showOnPopular}
                    onChange={handleChange}
                  />
                  <span>Popular Categories</span>
                </label>
                <label className="flag-checkbox">
                  <input
                    type="checkbox"
                    name="showOnRecentlyLaunched"
                    checked={form.showOnRecentlyLaunched}
                    onChange={handleChange}
                  />
                  <span>Recently Launched</span>
                </label>
              </div>

              <div className="form-actions-row">
                <button
                  type="button"
                  className="admin-btn ghost"
                  onClick={toggleForm}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="admin-btn primary"
                  disabled={submitting}
                >
                  {submitting
                    ? editingProductId
                      ? "Updating..."
                      : "Saving..."
                    : editingProductId
                      ? "Update Product"
                      : "Publish Product"}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      {!showForm && (
        <>
          <div className="admin-card admin-filters-card">
            <div className="filter-row">
              <div className="filter-item">
                <label className="filter-label">Search</label>
                <input
                  className="admin-input"
                  type="text"
                  placeholder="Search by name, SKU, ID…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              <div className="filter-item">
                <label className="filter-label">Category</label>
                <select
                  className="admin-input"
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  disabled={categoriesLoading}
                >
                  <option value="">All Categories</option>
                  {categories.map((cat) => (
                    <option key={cat.category_id} value={cat.category_id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
                {categoriesError && (
                  <small className="error-text">{categoriesError}</small>
                )}
              </div>

              <div className="filter-item stats">
                <p>
                  <strong>Visible products:</strong> {totalVisibleProducts}
                </p>
                {selectedCategory && (
                  <p>
                    <strong>{selectedCategory.name}</strong>{" "}
                    {totalStockForSelectedCategory !== null && (
                      <> – Total stock: {totalStockForSelectedCategory} units</>
                    )}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="admin-card admin-products-table-card">
            {loading ? (
              <p>Loading products…</p>
            ) : filteredProducts.length === 0 ? (
              <p>No products match the current filters.</p>
            ) : (
              <table className="admin-table pretty-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Category</th>
                    <th>Price (MRP)</th>
                    <th>Stock</th>
                    <th>Visibility</th>
                    <th>Flags</th>
                    <th className="actions-col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.map((p) => {
                    const stockVal = extractStock(p);
                    const isFeatured = Array.isArray(p.display_flags)
                      ? p.display_flags.includes("featured")
                      : false;
                    const showOnHome = Array.isArray(p.display_flags)
                      ? p.display_flags.includes("home")
                      : false;
                    const showOnPopular = Array.isArray(p.display_flags)
                      ? p.display_flags.includes("popular")
                      : false;
                    const showOnRecentlyLaunched = Array.isArray(
                      p.display_flags,
                    )
                      ? p.display_flags.includes("recently_launched")
                      : false;
                    const mainImage =
                      Array.isArray(p.images) && p.images.length > 0
                        ? p.images[0]
                        : null;
                    const initial = (p.name || "?").charAt(0).toUpperCase();

                    return (
                      <tr key={p.product_id || p._id}>
                        <td>
                          <div className="product-cell">
                            <div className="product-avatar with-image">
                              {mainImage ? (
                                <img
                                  src={mainImage}
                                  alt={p.name}
                                  className="product-avatar-image"
                                  onError={(e) =>
                                    (e.target.style.display = "none")
                                  }
                                />
                              ) : (
                                <span className="product-avatar-initial">
                                  {initial}
                                </span>
                              )}
                            </div>
                            <div>
                              <div className="product-name">{p.name}</div>
                              <div className="product-sub">
                                {p.sku || p.product_id || "-"}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>{getCategoryName(p.category_id)}</td>
                        <td>
                          {p.old_price > 0 && (
                            <span className="old-price">₹{p.old_price}</span>
                          )}
                          <span className="current-price">
                            ₹{p.price_display}
                          </span>
                        </td>
                        <td>{stockVal === null ? "-" : stockVal}</td>

                        <td>
                          <button
                            onClick={() => handleToggleVisibility(p)}
                            style={{
                              padding: "4px 10px",
                              borderRadius: "20px",
                              border: "none",
                              cursor: "pointer",
                              fontWeight: "bold",
                              fontSize: "12px",
                              backgroundColor: p.is_visible
                                ? "#dcfce7"
                                : "#fee2e2",
                              color: p.is_visible ? "#166534" : "#991b1b",
                              transition: "all 0.2s",
                            }}
                          >
                            {p.is_visible ? "Live 🟢" : "Hidden 🔴"}
                          </button>
                        </td>
                        <td>
                          <div className="flags-badges">
                            {isFeatured && (
                              <span className="badge badge-featured">
                                Featured
                              </span>
                            )}
                            {showOnHome && (
                              <span className="badge badge-home">Home</span>
                            )}
                            {showOnPopular && (
                              <span className="badge badge-popular">
                                Popular
                              </span>
                            )}
                            {showOnRecentlyLaunched && (
                              <span className="badge badge-recently-launched">
                                Recently Launched
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="actions-col">
                          <button
                            className="admin-btn secondary"
                            onClick={() => handleEdit(p)}
                          >
                            Edit
                          </button>
                          <button
                            className="admin-btn danger"
                            onClick={() => handleDelete(p)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {lightboxOpen && (
        <div
          className="lightbox-overlay"
          onClick={(e) => {
            if (e.target.classList.contains("lightbox-overlay"))
              closeLightbox();
          }}
        >
          <div className="lightbox-content" role="dialog" aria-modal="true">
            <button
              className="lightbox-close"
              aria-label="Close image preview"
              onClick={closeLightbox}
            >
              ✕
            </button>
            <div className="lightbox-image-wrapper">
              <img
                src={lightboxSrc}
                alt={lightboxAlt}
                className="lightbox-image"
                onError={(e) => (e.target.style.display = "none")}
              />
            </div>
            {lightboxAlt && (
              <div className="lightbox-caption">{lightboxAlt}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProductsPage;
